import React, { useEffect, useState } from "react";
import { Plug, CheckCircle2, AlertCircle, Download, RefreshCw, Trash2, Pause, Play, BookOpen } from "lucide-react";
import { odooApi, formatApiError } from "../../lib/ledgerApi";
import { toast } from "sonner";

function StatusPill({ status }) {
  const map = {
    connected: { color: "border-status-ok/40 bg-status-ok/10 text-status-ok", label: "Connected" },
    disabled: { color: "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400", label: "Disabled" },
    not_configured: { color: "border-border bg-muted/30 text-muted-foreground", label: "Not configured" },
  };
  const v = map[status] || map.not_configured;
  return <span className={`px-2 py-0.5 border rounded-sm text-[10px] uppercase tracking-widest ${v.color}`}>{v.label}</span>;
}

export default function OdooIntegration() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ base_url: "", db_name: "", login: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [pullResult, setPullResult] = useState(null);

  const load = async () => {
    try {
      const r = await odooApi.status();
      setStatus(r);
    } catch (e) {
      toast.error("Failed to load status", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onTest = async () => {
    setBusy(true);
    try {
      const r = await odooApi.testConnection(form);
      if (r.ok) toast.success("Connection OK", { description: `Odoo ${r.server_version || ""}` });
      else toast.error("Connection failed", { description: `${r.code}: ${r.message}` });
    } catch (e) {
      toast.error("Test failed", { description: formatApiError(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  const onSave = async () => {
    setBusy(true);
    try {
      await odooApi.saveConnection(form);
      toast.success("Saved");
      setShowForm(false);
      setForm({ base_url: "", db_name: "", login: "", password: "" });
      load();
    } catch (e) {
      toast.error("Save failed", { description: formatApiError(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  const onTogglePause = async () => {
    try {
      if (status?.enabled) await odooApi.disable();
      else await odooApi.enable();
      load();
    } catch (e) {
      toast.error("Update failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Delete the Odoo connection?")) return;
    try {
      await odooApi.deleteConnection();
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  const onPull = async () => {
    setBusy(true);
    try {
      const r = await odooApi.pullInvoices();
      setPullResult(r);
      toast.success(`Pulled ${r.pulled} (${r.created.length} new, ${r.skipped.length} skipped)`);
    } catch (e) {
      toast.error("Pull failed", { description: formatApiError(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  if (loading) return <div className="p-10 text-sm text-muted-foreground">Loading…</div>;

  const pillStatus = !status?.configured ? "not_configured"
    : (status?.enabled ? "connected" : "disabled");

  return (
    <div className="max-w-5xl px-6 md:px-10 py-10 md:py-14" data-testid="odoo-integration">
      <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">/ Integrations</div>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-medium flex items-center gap-3">
            <Plug className="w-6 h-6" strokeWidth={1.5} /> Odoo
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Sync NeoPay invoices, transfers, and AI-agent payments into Odoo Accounting in real time.
            HMAC-signed webhooks land in your <code className="font-mono">account.move</code> chatter
            with full <code className="font-mono">proof_url</code> + <code className="font-mono">federation_verified</code> provenance.
          </p>
        </div>
        <a
          href="/api/integrations/odoo/spec/markdown"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="odoo-spec-link"
          className="text-xs border border-border px-3 py-2 rounded-sm hover:border-foreground inline-flex items-center gap-1.5"
        >
          <BookOpen className="w-3.5 h-3.5" strokeWidth={1.5} /> Setup guide
        </a>
      </div>

      {/* Connection card */}
      <div className="border border-border rounded-sm bg-card p-5 mb-6" data-testid="odoo-connection-card">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div className="flex items-center gap-3">
            <span className="text-sm">Connection</span>
            <StatusPill status={pillStatus} />
          </div>
          <div className="flex gap-2">
            {status?.configured && (
              <>
                <button onClick={onTogglePause} className="text-xs border border-border px-3 py-1.5 rounded-sm hover:border-foreground inline-flex items-center gap-1.5" data-testid="odoo-toggle">
                  {status.enabled ? <><Pause className="w-3 h-3" strokeWidth={1.5} /> Disable</> : <><Play className="w-3 h-3" strokeWidth={1.5} /> Enable</>}
                </button>
                <button onClick={onDelete} className="text-xs border border-border px-3 py-1.5 rounded-sm hover:border-status-tampered text-muted-foreground hover:text-status-tampered inline-flex items-center gap-1.5" data-testid="odoo-delete">
                  <Trash2 className="w-3 h-3" strokeWidth={1.5} /> Delete
                </button>
              </>
            )}
            {!status?.configured && (
              <button onClick={() => setShowForm(true)} className="text-xs border border-foreground bg-foreground text-background px-3 py-1.5 rounded-sm uppercase tracking-widest" data-testid="odoo-connect-btn">
                Set up Odoo
              </button>
            )}
          </div>
        </div>

        {status?.configured && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm" data-testid="odoo-conn-details">
            <div><span className="text-muted-foreground text-xs">Base URL</span><div className="font-mono text-xs">{status.connection?.base_url}</div></div>
            <div><span className="text-muted-foreground text-xs">Database</span><div className="font-mono text-xs">{status.connection?.db_name}</div></div>
            <div><span className="text-muted-foreground text-xs">Login</span><div className="font-mono text-xs">{status.connection?.login}</div></div>
            <div><span className="text-muted-foreground text-xs">Updated by</span><div className="font-mono text-xs">{status.connection?.updated_by}</div></div>
          </div>
        )}

        {!status?.configured && !showForm && (
          <div className="text-sm text-muted-foreground">
            Install the <code className="font-mono">neopay_connector</code> Odoo addon, paste your Odoo
            base URL + database + admin credentials here, and we'll auto-register a webhook for you.
          </div>
        )}

        {showForm && (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="odoo-form">
            <input value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                   placeholder="https://your-odoo.example.com" data-testid="odoo-base-url"
                   className="bg-background border border-border rounded-sm px-3 py-2 text-sm font-mono"/>
            <input value={form.db_name} onChange={(e) => setForm({ ...form, db_name: e.target.value })}
                   placeholder="acme_production" data-testid="odoo-db-name"
                   className="bg-background border border-border rounded-sm px-3 py-2 text-sm font-mono"/>
            <input value={form.login} onChange={(e) => setForm({ ...form, login: e.target.value })}
                   placeholder="admin@acme.com" data-testid="odoo-login"
                   className="bg-background border border-border rounded-sm px-3 py-2 text-sm"/>
            <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                   placeholder="API key or password" data-testid="odoo-password"
                   className="bg-background border border-border rounded-sm px-3 py-2 text-sm"/>
            <div className="sm:col-span-2 flex gap-2 justify-end">
              <button onClick={() => setShowForm(false)} className="text-xs text-muted-foreground hover:text-foreground px-3 py-2">Cancel</button>
              <button onClick={onTest} disabled={busy || !form.base_url} className="text-xs border border-border px-3 py-2 rounded-sm hover:border-foreground" data-testid="odoo-test-btn">Test</button>
              <button onClick={onSave} disabled={busy || !form.base_url || !form.db_name || !form.login || !form.password} className="text-xs border border-foreground bg-foreground text-background px-4 py-2 rounded-sm uppercase tracking-widest" data-testid="odoo-save-btn">
                {busy ? "Saving…" : "Test & save"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Sync stats */}
      {status?.configured && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6" data-testid="odoo-stats">
          <div className="border border-border rounded-sm bg-card p-4">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Events delivered</div>
            <div className="text-2xl font-mono mt-1">{status.total_events}</div>
          </div>
          <div className="border border-border rounded-sm bg-card p-4">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Last event</div>
            <div className="text-sm font-mono mt-1">{status.last_event_at ? new Date(status.last_event_at).toLocaleString() : "—"}</div>
          </div>
          <div className="border border-border rounded-sm bg-card p-4">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Status</div>
            <div className="text-sm mt-1 inline-flex items-center gap-1.5">
              {status.enabled ? <CheckCircle2 className="w-4 h-4 text-status-ok" strokeWidth={1.5} /> : <AlertCircle className="w-4 h-4 text-amber-500" strokeWidth={1.5} />}
              {status.enabled ? "Active" : "Disabled"}
            </div>
          </div>
        </div>
      )}

      {/* Pull invoices */}
      {status?.configured && status?.enabled && (
        <div className="border border-border rounded-sm bg-card p-5 mb-6">
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div>
              <div className="text-sm">Pull open Odoo invoices</div>
              <div className="text-xs text-muted-foreground mt-1">
                Fetch posted, unpaid customer invoices from your Odoo and mirror them on NeoPay so you can mint pay-links from any of them.
              </div>
            </div>
            <button onClick={onPull} disabled={busy} className="text-xs border border-foreground bg-foreground text-background px-4 py-2 rounded-sm uppercase tracking-widest inline-flex items-center gap-1.5" data-testid="odoo-pull-btn">
              <Download className="w-3 h-3" strokeWidth={1.5} /> {busy ? "Pulling…" : "Pull now"}
            </button>
          </div>
          {pullResult && (
            <div className="mt-3 text-xs border border-border bg-background rounded-sm p-3" data-testid="odoo-pull-result">
              <div>Pulled: <span className="font-mono">{pullResult.pulled}</span></div>
              <div>New: <span className="font-mono">{pullResult.created?.length || 0}</span></div>
              <div>Skipped (already linked): <span className="font-mono">{pullResult.skipped?.length || 0}</span></div>
              {pullResult.created?.length > 0 && (
                <details className="mt-2"><summary className="cursor-pointer text-muted-foreground">First 5 invoices</summary>
                  <ul className="mt-1 space-y-0.5 font-mono">
                    {pullResult.created.slice(0, 5).map((c) => (
                      <li key={c.odoo_id}>{c.name} — {c.amount}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      )}

      {/* Setup guide */}
      <div className="border border-dashed border-border rounded-sm p-5 text-xs text-muted-foreground space-y-2">
        <div className="text-foreground text-sm">Quick setup</div>
        <ol className="list-decimal list-inside space-y-1">
          <li>Install the <code className="font-mono">neopay_connector</code> addon in your Odoo (16 or 17).</li>
          <li>In Odoo: Settings → Accounting → NeoPay Connector → paste API key → click <em>Test connection</em>.</li>
          <li>Click <em>Register webhook</em> on the addon → it auto-creates the Phase 8 subscription on NeoPay.</li>
          <li>Optionally come back here and click <em>Pull invoices now</em> to mirror your existing open Odoo invoices.</li>
        </ol>
        <a href="/api/integrations/odoo/spec/markdown" target="_blank" rel="noopener noreferrer" className="underline">Full setup guide →</a>
      </div>
    </div>
  );
}
